import React, { useState, useRef, useEffect } from 'react';
import { Battery, Thermometer, HardDrive, AlertCircle,Clock } from 'lucide-react';

const DeviceDetailsTimeline = ({ 
  deviceDetails, 
  recordingStart, 
  recordingEnd, 
  deviceId,
  recordingId,
  deviceRecordingStart,  // Add these new props
  deviceRecordingEnd     // for actual device recording times
}) => {
  const [activeTooltip, setActiveTooltip] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState('left-50');
  const containerRef = useRef(null);
  const tooltipRef = useRef(null);

  const formatDuration = (startTime, endTime) => {
    const durationInSeconds = endTime - startTime;
    const minutes = Math.floor(durationInSeconds / 60);
    const seconds = durationInSeconds % 60;
    return `${minutes}m ${seconds}s`;
  };

  const calculatePosition = (timestamp) => {
    const totalDuration = recordingEnd - recordingStart;
    const relativePosition = ((timestamp - recordingStart) / totalDuration) * 100;
    return Math.min(Math.max(relativePosition, 0), 100);
  };

  // Filter details to only show those within recording period
  const getValidDetails = () => {
    return deviceDetails
      .filter(detail => detail.createdAt >= recordingStart && detail.createdAt <= recordingEnd)
      .sort((a, b) => a.createdAt - b.createdAt);
  };


  const getStatusInfo = (detail) => {
    if (['SEVERE', 'CRITICAL', 'EMERGENCY', 'SHUTDOWN'].includes(detail.thermalStatus)) {
      return {
        icon: <Thermometer size={16} />,
        color: '#dc3545',
        backgroundColor: '#f8d7da',
        status: 'Critical Temperature'
      };
    }
    
    if (detail.batteryLevel < 20) {
      return {
        icon: <Battery size={16} />,
        color: '#dc3545',
        backgroundColor: '#f8d7da',
        status: 'Low Battery'
      };
    }

    if (detail.space < 1000) {
      return {
        icon: <HardDrive size={16} />,
        color: '#fd7e14',
        backgroundColor: '#fff3cd',
        status: 'Low Storage'
      };
    }

    return {
      icon: <AlertCircle size={16} />,
      color: '#198754',
      backgroundColor: '#d1e7dd',
      status: 'Normal'
    };
  };

  const CustomTooltip = ({ detail, status }) => {
    useEffect(() => {
      if (tooltipRef.current && containerRef.current) {
        const tooltipRect = tooltipRef.current.getBoundingClientRect();
        const containerRect = containerRef.current.getBoundingClientRect();
        const viewportWidth = window.innerWidth;

        // Check if tooltip goes beyond the right edge
        if (tooltipRect.right > viewportWidth - 20) {
          setTooltipPosition('right');
        } 
        // Check if tooltip goes beyond the left edge
        else if (tooltipRect.left < 20) {
          setTooltipPosition('left');
        }
        else {
          setTooltipPosition('center');
        }
      }
    }, []);

    const getTooltipStyles = () => {
      const baseStyles = {
        position: 'absolute',
        bottom: '130%',
        backgroundColor: 'white',
        borderRadius: '8px',
        padding: '12px',
        fontSize: '12px',
        whiteSpace: 'nowrap',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
        zIndex: 1000,
        border: '1px solid rgba(0, 0, 0, 0.05)',
        minWidth: '200px',
        backdropFilter: 'blur(8px)'
      };

      switch (tooltipPosition) {
        case 'right':
          return {
            ...baseStyles,
            right: 0,
            transform: 'translateX(0)'
          };
        case 'left':
          return {
            ...baseStyles,
            left: 0,
            transform: 'translateX(0)'
          };
        default:
          return {
            ...baseStyles,
            left: '50%',
            transform: 'translateX(-50%)'
          };
      }
    };

    const getArrowStyles = () => {
      const baseStyles = {
        position: 'absolute',
        bottom: '-6px',
        borderLeft: '6px solid transparent',
        borderRight: '6px solid transparent',
        borderTop: '6px solid white',
        filter: 'drop-shadow(0 2px 1px rgba(0,0,0,0.1))'
      };

      switch (tooltipPosition) {
        case 'right':
          return {
            ...baseStyles,
            right: '10px'
          };
        case 'left':
          return {
            ...baseStyles,
            left: '10px'
          };
        default:
          return {
            ...baseStyles,
            left: '50%',
            marginLeft: '-6px'
          };
      }
    };

    return (
      <div ref={tooltipRef} style={getTooltipStyles()}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          marginBottom: '8px',
          padding: '4px 8px',
          backgroundColor: status.backgroundColor,
          borderRadius: '4px',
          color: status.color
        }}>
          {status.icon}
          <span style={{ marginLeft: '8px', fontWeight: 500 }}>{status.status}</span>
        </div>

        <div style={{ display: 'grid', gap: '8px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ color: '#6b7280' }}>Recording Time:</span>
            <span style={{ fontWeight: 500 }}>{formatDuration(recordingStart, detail.createdAt)}</span>
          </div>
          
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ color: '#6b7280' }}>Battery:</span>
            <span style={{ fontWeight: 500 }}>
              {detail.batteryLevel}% 
              <span style={{ color: '#6b7280', marginLeft: '4px', fontWeight: 'normal' }}>
                ({detail.batteryStatus})
              </span>
            </span>
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ color: '#6b7280' }}>Storage:</span>
            <span style={{ fontWeight: 500 }}>{(detail.space / 1024).toFixed(2)} GB</span>
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ color: '#6b7280' }}>Thermal:</span>
            <span style={{ fontWeight: 500 }}>{detail.thermalStatus || 'Normal'}</span>
          </div>
        </div>

        <div style={getArrowStyles()} />
      </div>
    );
  };

  const sortedDetails = getValidDetails();
  const deviceStart = sortedDetails[0]?.createdAt;
  const deviceEnd = sortedDetails[sortedDetails.length - 1]?.createdAt;

  const timelineId = `${recordingId}-${deviceId}`;


  return (
    <div className="mt-3 mb-4" ref={containerRef}>
      <div style={{ position: 'relative', height: '60px' }}>
        {/* Device Recording Duration */}
        <div style={{
          fontSize: '12px',
          color: '#6c757d',
          marginBottom: '4px',
          textAlign: 'right'
        }}>
          Device Recording Time: {formatDuration(deviceRecordingStart, deviceRecordingEnd)}
        </div>

        {/* Timeline base */}
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '0',
          right: '0',
          height: '2px',
          backgroundColor: '#e9ecef',
          transform: 'translateY(-50%)',
          borderRadius: '1px'
        }} />

        {/* Device recording period indicator */}
        <div style={{
          position: 'absolute',
          top: '50%',
          left: `${calculatePosition(deviceRecordingStart)}%`,
          width: `${calculatePosition(deviceRecordingEnd) - calculatePosition(deviceRecordingStart)}%`,
          height: '4px',
          backgroundColor: '#4e73df',
          transform: 'translateY(-50%)',
          borderRadius: '2px',
          opacity: 0.3
        }} />

        {/* Timeline points */}
        {sortedDetails.map((detail, index) => {
          const position = calculatePosition(detail.createdAt);
          const status = getStatusInfo(detail);
          const pointId = `${timelineId}-${index}`;

          return (
            <div 
              key={index}
              style={{
                position: 'absolute',
                top: '50%',
                left: `${position}%`,
                transform: 'translate(-50%, -50%)',
              }}
            >
              <div
                style={{
                  width: '24px',
                  height: '24px',
                  backgroundColor: status.backgroundColor,
                  border: `2px solid ${status.color}`,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  color: status.color,
                  position: 'relative',
                  zIndex: activeTooltip === pointId ? 2 : 1,
                  transition: 'transform 0.2s ease',
                  transform: activeTooltip === pointId ? 'scale(1.1)' : 'scale(1)'
                }}
                onMouseEnter={() => setActiveTooltip(pointId)}
                onMouseLeave={() => setActiveTooltip(null)}
              >
                {status.icon}
                {activeTooltip === pointId && (
                  <CustomTooltip 
                    detail={detail} 
                    status={status}
                  />
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default DeviceDetailsTimeline;